import { Channel, Socket } from "phoenix";
import * as fetcher from "common/api/Fetcher";
import { AppMessageChannel } from "common/api/appMessageChannel.service";
import { Nullable } from "common/types";
import { RootDispatchType, store } from "common/store";
import { RootActions } from "common/reducers";
import { createAction } from "common/reducers/Utils";
import config from "react-global-configuration";
import { ITaskData } from "common/records/Task";
import { syncTask } from "common/helpers/TaskSync";
import { isExpired } from "common/services/jwtToken.service";
import { AuthAsyncActions } from "common/reducers/auth.async.actions";
import { AuthActions } from "common/reducers/auth.actions";
import { trackWsEvent, noticeError } from "common/helpers/NewRelic";

export type RemoteStorageCallback = (data: IRemoteStorageStateChange, dispatch: RootDispatchType) => void;
export type RemoteStorageErrorCallback = (response?: any) => any;
export type RemoteStorageTimeoutCallback = (response?: any) => any;

export interface IRemoteStorageServiceOptions {
  callbacks: RemoteStorageCallback[];
  onError?: RemoteStorageErrorCallback;
  onTimeout?: RemoteStorageTimeoutCallback;
  dispatch: RootDispatchType;
}

export interface IRemoteStorageStateChange {
  change: { [key: string]: any };
  kind: "clear" | "full" | "push" | "remove";
  node: string;
  sessionId: string;
  timestamp: Date;
}

export interface IRemoteStorageService {
  initialized: boolean;
  connect: (sessionId: string) => Promise<boolean>;
  clear: () => Promise<boolean>;
  disconnect: () => void;
  endSession: () => Promise<boolean>;
  getItem: (key: string) => Promise<boolean>;
  removeItem: (key: string) => Promise<boolean>;
  setItem: (key: string, value: any) => Promise<boolean>;
  appMessages: AppMessageChannel;
}

/**
 * A class to facilitate syncing values between browser instances
 */
export class RemoteStorageService implements IRemoteStorageService {
  public initialized = false;
  public appMessages: AppMessageChannel;

  private callbacks: RemoteStorageCallback[];
  private channel: Nullable<Channel>;
  private taskChannel: Nullable<Channel>;
  private errorCallback: RemoteStorageErrorCallback;
  private socket: Nullable<Socket>;
  private socketUrl: string;
  private timeout = 2_000;
  private timeoutCallback: RemoteStorageTimeoutCallback;
  private dispatch;
  private connectCount = 0;

  constructor(options: IRemoteStorageServiceOptions) {
    this.socketUrl = config.get("RSF_ANALYTICS_WS") + "/socket";
    this.callbacks = options.callbacks;
    this.dispatch = options.dispatch;

    this.errorCallback = options.onError || this.defaultErrorHandler;
    this.timeoutCallback = options.onTimeout || this.defaultTimeoutHandler;

    this.appMessages = new AppMessageChannel({
      dispatch: options.dispatch,
      socket: () => (this.initialized ? this.socket : null),
      leaveTimeout: this.timeout,
    });
  }

  /**
   * Connect to the server and subscribe to new data being pushed down from the remote storage.
   */
  public async connect(sessionId: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      // If the channel has already been initialized, unsubscribe from the existing listeners
      if (this.channel) {
        this.channel.off("session:disconnect");
        this.channel.off("state:changed");
      }

      if (this.socket && this.socket.isConnected()) {
        this.socket.disconnect();
      }

      // Phoenix calls a `params` function on every transport open, so each reconnect re-reads the JWT.
      // Socket-level auth is what assigns the user the app_messages channel requires.
      this.socket = new Socket(this.socketUrl, { params: () => ({ token: fetcher.token() }) });
      this.setupSocketEventHandlers();
      this.socket.connect();
      const params = () => ({ bearer: fetcher.token() });
      const topic = `remote_storage:${sessionId}`;
      this.channel = this.socket.channel(topic, params);
      this.channel.on("session:disconnect", () => this.disconnect());
      this.channel.on("state:changed", this.processStateChange.bind(this));
      this.channel
        .join()
        .receive("ok", () => resolve(true))
        .receive("error", (reasons) => reject(reasons))
        .receive("timeout", () => reject("A timeout occurred"));

      const taskTopic = `task_sync:${sessionId}`;
      this.taskChannel = this.socket.channel(taskTopic, params);
      this.taskChannel.on("task_update_sync", async (data) => {
        const task: ITaskData = data.data;
        if (task) await syncTask(task);
      });

      this.taskChannel
        .join()
        .receive("ok", () => resolve(true))
        .receive("error", (reasons) => reject(reasons))
        .receive("timeout", () => reject("A timeout occurred"));

      this.initialized = true;

      void this.appMessages.join();
    });
  }

  /**
   * Deletes all state for the session on the remote server
   * */
  public async clear(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("storage:clear_state", {}, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false))
        // Client code callbacks
        .receive("error", this.errorCallback)
        .receive("timeout", this.timeoutCallback);
    });
  }

  /**
   * Asks the server to send out debug information about the custer status to all clients
   * connected to the session. The callback function will be called on "ok", "error" and "timeout".
   * The only place to view the response is in the Developer Tools Network tab (in the web socket
   * output).
   */
  public async debugCluster(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("debug:cluster", {}, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false));
    });
  }

  /**
   * Disconnects from the server but does not delete the remote storage for this
   * session. Consider calling `endSession()` instead to clean up the server.
   */
  public disconnect(): void {
    this.initialized = false;

    this.appMessages.leave();

    if (this.channel) {
      this.channel.leave(this.timeout);
    }

    if (this.socket) {
      this.socket.disconnect();
    }
  }

  /**
   * Sends a message to the server to delete this session data and disconnect
   * any other remote storage sessions
   * */
  public async endSession(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("session:disconnect", {}, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false));
    });
  }

  /**
   * Request an item to be pushed out to all clients for the session.
   * **Note:** This does not return the value from this method
   */
  public async getItem(key: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("storage:get_item", { data: key }, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false))
        // Client code callbacks
        .receive("error", this.errorCallback)
        .receive("timeout", this.timeoutCallback);
    });
  }

  /** Removes an item from the remote storage */
  public async removeItem(key: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("storage:remove_item", { data: key }, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false))
        // Client code callbacks
        .receive("error", this.errorCallback)
        .receive("timeout", this.timeoutCallback);
    });
  }

  /**
   * Sets the key/value pair on the server. If the key already exists, it will be
   * overwritten. Changes are automatically pushed out to session clients.
   */
  public async setItem(key: string, value: any): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.channel) {
        resolve(false);
        return;
      }

      this.channel
        .push("storage:set_item", { data: { [key]: value } }, this.timeout)
        .receive("ok", () => resolve(true))
        .receive("error", () => resolve(false))
        .receive("timeout", () => resolve(false))
        // Client code callbacks
        .receive("error", this.errorCallback)
        .receive("timeout", this.timeoutCallback);
    });
  }

  private setupSocketEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onOpen(() => {
      this.connectCount += 1;

      trackWsEvent("WsOpen", {
        attempt: this.connectCount,
        isReconnect: this.connectCount > 1,
      });
    });

    this.socket.onClose((event) => {
      trackWsEvent("WsClose", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        isReconnect: this.connectCount > 1,
      });
    });

    this.socket.onError(async (e, _t) => {
      const state = store.getState();
      const token = state.getIn(["auth", "token"]) as string;

      if (isExpired(token)) {
        trackWsEvent("WsTokenExpired", {
          isReconnect: this.connectCount > 0,
        });

        const refreshToken = state.getIn(["auth", "refreshToken"]) as string;

        try {
          await store.dispatch(AuthAsyncActions.refreshToken(refreshToken));
        } catch (err) {
          trackWsEvent("WsTokenRefreshFailed", {
            refreshErrorClass: (err as any)?.name,
            refreshErrorMessage: (err as any)?.message?.slice(0, 200), // avoid huge payloads
          });

          store.dispatch(AuthActions.setRefreshTokenExpired(true));
        }
      } else {
        noticeError(e, {
          context: "websocket.onError",
        });
      }

      this.errorCallback(e);
    });
  }

  /** Generic error handler that throws an exception */
  private defaultErrorHandler(err: any): never {
    throw {
      message: "Error occurred: Consider including an error callback.",
      error: err,
    };
  }

  /** Generic timeout handler that throws an exception */
  private defaultTimeoutHandler(err: any): never {
    throw {
      message: "Timeout occurred: Consider including a timeout callback.",
      error: err,
    };
  }

  /** Iterates over the callbacks that were registers during object creation */
  private processStateChange(res: any): void {
    // Massage the values into appropriate shapes
    res.data.timestamp = new Date(res.data.timestamp);
    res.data.sessionId = res.data.session_id;
    delete res.data.session_id;

    for (const cb of this.callbacks) {
      // Send the payload to the subscriber
      cb(res.data, this.dispatch);
    }
  }
}

export const remoteStorageCallback = (data: any, dispatch: RootDispatchType): void => {
  switch (data.kind) {
    case "full":
    case "push":
    case "clear":
      if (data.change) {
        Object.keys(data.change).forEach((key) => {
          const action = () => createAction(key, data.change[key]);
          dispatch(action() as RootActions);
        });
      }
    // no default
  }
};
